import {
  Box,
  Button,
  Flex,
  PopoverHeader,
  Select,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { DictionaryOption } from '../../interface/api';
import { INK, INK_MUTED } from '../../styles/tokens';

const INTER_STACK =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export type FacilityEditStatus = 'loading' | 'ready' | 'unavailable' | 'saving';

export type FacilityEditFormProps = {
  readonly currentValue: string;
  readonly department: string;
  readonly options: readonly DictionaryOption[];
  readonly status: FacilityEditStatus;
  readonly errorMessage?: string;
  readonly canSave: boolean;
  readonly selectRef: React.RefObject<HTMLSelectElement>;
  readonly onSave: (value: string) => void;
  readonly onCancel: () => void;
};

const WRITE_UNAVAILABLE = "Saving isn't available for this case.";

export const FacilityEditForm: React.FC<FacilityEditFormProps> = ({
  currentValue,
  department,
  options,
  status,
  errorMessage,
  canSave,
  selectRef,
  onSave,
  onCancel
}) => {
  const [selection, setSelection] = React.useState(currentValue);

  const isSaving = status === 'saving';
  const notice = errorMessage ?? (canSave ? undefined : WRITE_UNAVAILABLE);
  const currentIsListed = options.some(
    (option) => option.value === currentValue
  );

  return (
    <Box
      data-testid="facility-edit-form"
    >
      <PopoverHeader
        border="none"
        padding="0 0 8px"
        fontFamily={INTER_STACK}
        fontSize="12.5px"
        fontWeight={700}
        color={INK}
      >
        Edit facility
      </PopoverHeader>
      <Select
        ref={selectRef}
        data-testid="facility-edit-select"
        aria-label="Facility"
        size="sm"
        fontFamily={INTER_STACK}
        fontSize="12px"
        value={selection}
        isDisabled={status !== 'ready'}
        onChange={(event) => setSelection(event.target.value)}
      >
        {!currentIsListed && (
          <option value={currentValue}>{currentValue || 'N/A'}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <Text
        as="p"
        data-testid="billing-location-department"
        fontFamily={INTER_STACK}
        fontSize="11px"
        fontWeight={400}
        color={INK_MUTED}
        marginTop="6px"
      >
        {department}
      </Text>
      {notice && (
        <Text
          as="p"
          role="alert"
          data-testid="facility-edit-error"
          fontFamily={INTER_STACK}
          fontSize="11px"
          fontWeight={500}
          color="red.600"
          marginTop="6px"
        >
          {notice}
        </Text>
      )}
      <Flex justifyContent="flex-end" gap="6px" marginTop="10px">
        <Button
          data-testid="facility-edit-cancel"
          size="xs"
          variant="ghost"
          fontFamily={INTER_STACK}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid="facility-edit-save"
          size="xs"
          colorScheme="blue"
          fontFamily={INTER_STACK}
          isLoading={isSaving}
          isDisabled={
            !canSave || status !== 'ready' || selection === currentValue
          }
          onClick={() => onSave(selection)}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};
